// src/components/EditMatch.js
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FiEdit3, FiSearch, FiSave } from 'react-icons/fi';
import matchService from '../services/matchService';
import tournamentService from '../services/tournamentService';
import adminService from '../services/adminService';
import '../styles/AdminDashboard.css';

const MATCH_STATUS_GROUPS = [
    {
        label: 'Obecné',
        options: [
            ['NS', 'Nezačal (NS)'],
            ['TBD', 'Bude upřesněno (TBD)'],
            ['FT', 'Konec (FT)'],
            ['LIVE', 'Živě (LIVE)'],
            ['PST', 'Odloženo (PST)'],
            ['CANC', 'Zrušeno (CANC)'],
            ['ABD', 'Opuštěno (ABD)'],
            ['SUSP', 'Pozastaveno (SUSP)'],
            ['INT', 'Přerušeno (INT)'],
            ['INTR', 'Přerušeno (INTR)'],
            ['AWD', 'Kontumace (AWD)'],
            ['WO', 'Kontumace (WO)'],
            ['AW', 'Výhra hostů (AW)'],
            ['POST', 'POST'],
        ],
    },
    {
        label: 'Fotbal',
        options: [
            ['_1H', '1. poločas'],
            ['HT', 'Poločas (HT)'],
            ['_2H', '2. poločas'],
            ['ET', 'Prodloužení (ET)'],
            ['BT', 'Přestávka (BT)'],
            ['P', 'Penalty – probíhá (P)'],
            ['AET', 'Po prodloužení (AET)'],
            ['PEN', 'Po penaltách (PEN)'],
        ],
    },
    {
        label: 'Hokej',
        options: [
            ['P1', '1. třetina (P1)'],
            ['P2', '2. třetina (P2)'],
            ['P3', '3. třetina (P3)'],
            ['OT', 'Prodloužení (OT)'],
            ['PT', 'Nájezdy (PT)'],
            ['AOT', 'Po prodloužení (AOT)'],
            ['AP', 'Po nájezdech (AP)'],
        ],
    },
];

const toLocalInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
};

const fromLocalInput = (value) => (value ? new Date(value).toISOString() : null);

const toNum = (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const n = Number(value);
    return Number.isNaN(n) ? null : n;
};

const formatMatchLabel = (m) => {
    const home = m.homeTeam?.name || '?';
    const away = m.awayTeam?.name || '?';
    const date = m.date ? new Date(m.date).toLocaleString() : '—';
    return `${home} – ${away} (${date})`;
};

const EditMatch = () => {
    const [tournaments, setTournaments] = useState([]);
    const [allMatches, setAllMatches] = useState([]);

    const [tournamentId, setTournamentId] = useState('');
    const [round, setRound] = useState('');
    const [matchId, setMatchId] = useState('');

    const [loadedMatch, setLoadedMatch] = useState(null);
    const [loadingMatch, setLoadingMatch] = useState(false);
    const [saving, setSaving] = useState(false);

    // Editovatelná pole
    const [date, setDate] = useState('');
    const [matchStatus, setMatchStatus] = useState('NS');
    const [canGoToOvertime, setCanGoToOvertime] = useState(false);
    const [scoreHome, setScoreHome] = useState('');
    const [scoreAway, setScoreAway] = useState('');
    const [regHome, setRegHome] = useState('');
    const [regAway, setRegAway] = useState('');
    const [etHome, setEtHome] = useState('');
    const [etAway, setEtAway] = useState('');
    const [penHome, setPenHome] = useState('');
    const [penAway, setPenAway] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [tournamentData, matchData] = await Promise.all([
                    tournamentService.getAllTournaments(),
                    adminService.getEditableMatches(),
                ]);
                const sorted = (Array.isArray(tournamentData) ? tournamentData : [])
                    .slice()
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                setTournaments(sorted);
                setAllMatches(Array.isArray(matchData) ? matchData : []);
            } catch (e) {
                console.error('Failed to load data for edit match page', e);
                toast.error('Nepodařilo se načíst turnaje a zápasy');
            }
        };
        fetchData();
    }, []);

    // Turnaje, které mají alespoň jeden editovatelný zápas
    const editableTournamentIds = new Set(
        allMatches.flatMap(m => (m.tournaments || []).map(t => String(t.id)))
    );
    const visibleTournaments = tournaments.filter(t => editableTournamentIds.has(String(t.id)));

    const tournamentMatches = tournamentId
        ? allMatches.filter(m => (m.tournaments || []).some(t => String(t.id) === String(tournamentId)))
        : [];

    const rounds = Array.from(new Set(tournamentMatches.map(m => m.round || '—')))
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

    const matchesInRound = round
        ? tournamentMatches.filter(m => (m.round || '—') === round)
        : [];

    const populateForm = (m) => {
        setDate(toLocalInput(m.date));
        setMatchStatus(m.matchStatus || 'NS');
        setCanGoToOvertime(m.canGoToOvertime ?? false);
        setScoreHome(m.scoreHome ?? '');
        setScoreAway(m.scoreAway ?? '');
        setRegHome(m.regularTimeScoreHome ?? '');
        setRegAway(m.regularTimeScoreAway ?? '');
        setEtHome(m.extraTimeScoreHome ?? '');
        setEtAway(m.extraTimeScoreAway ?? '');
        setPenHome(m.penaltyScoreHome ?? '');
        setPenAway(m.penaltyScoreAway ?? '');
    };

    const handleTournamentChange = (e) => {
        setTournamentId(e.target.value);
        setRound('');
        setMatchId('');
        setLoadedMatch(null);
    };

    const handleRoundChange = (e) => {
        setRound(e.target.value);
        setMatchId('');
        setLoadedMatch(null);
    };

    const handleMatchChange = async (e) => {
        const id = e.target.value;
        setMatchId(id);
        setLoadedMatch(null);
        if (!id) return;
        setLoadingMatch(true);
        try {
            const data = await matchService.getMatchById(id);
            setLoadedMatch(data);
            populateForm(data);
        } catch (err) {
            console.error('Failed to load match', err);
            toast.error('Nepodařilo se načíst detail zápasu');
        } finally {
            setLoadingMatch(false);
        }
    };

    const handleSave = async () => {
        if (!loadedMatch) return;

        const payload = {
            ...loadedMatch,
            date: fromLocalInput(date),
            matchStatus,
            canGoToOvertime,
            scoreHome: toNum(scoreHome),
            scoreAway: toNum(scoreAway),
            regularTimeScoreHome: toNum(regHome),
            regularTimeScoreAway: toNum(regAway),
            extraTimeScoreHome: toNum(etHome),
            extraTimeScoreAway: toNum(etAway),
            penaltyScoreHome: toNum(penHome),
            penaltyScoreAway: toNum(penAway),
        };

        setSaving(true);
        try {
            const updated = await matchService.updateMatch(loadedMatch.id, payload);
            setLoadedMatch(updated || payload);
            setAllMatches(prev => prev.map(m => (String(m.id) === String(loadedMatch.id) ? { ...m, ...payload } : m)));
            toast.success('Zápas byl uložen');
        } catch (error) {
            console.error('Failed to update match', error);
            const detail = error?.response?.data || error?.message || 'Neznámá chyba';
            toast.error(`Nepodařilo se uložit zápas: ${detail}`);
            setSaving(false);
            return;
        }

        try {
            const message = await adminService.recalculatePointsForMatch(loadedMatch.id);
            toast.success(message || 'Body uživatelských tipů byly přepočítány');
        } catch (error) {
            console.error('Failed to recalculate points', error);
            const detail = error?.response?.data || error?.message || 'Neznámá chyba';
            toast.error(`Zápas uložen, ale body se nepodařilo přepočítat: ${detail}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="admin-dashboard-container">
            <h2 className="admin-section-title">Úprava zápasu</h2>

            <div className="admin-edit-stack">
                {/* Karta: Vyhledání zápasu */}
                <div className="admin-action-card">
                    <div className="admin-action-header">
                        <span className="admin-action-icon"><FiSearch /></span>
                        <h3>Vyhledat zápas</h3>
                    </div>
                    <p className="admin-action-desc">
                        V nabídce jsou zápasy navázané na turnaj, odehrané v posledním měsíci nebo naplánované do budoucna. Vyber zápas přes turnaj, kolo a konkrétní zápas.
                    </p>
                    <div className="admin-action-body">
                        <div className="admin-field">
                            <label>Turnaj</label>
                            <select
                                className="admin-select"
                                value={tournamentId}
                                onChange={handleTournamentChange}
                                disabled={visibleTournaments.length === 0}
                            >
                                <option value="">— Vyberte turnaj —</option>
                                {visibleTournaments.map(t => (
                                    <option key={t.id} value={t.id}>
                                        {t.name}{t.year ? ` (${t.year})` : ''} [{t.sportType}]
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="admin-field">
                            <label>Kolo</label>
                            <select
                                className="admin-select"
                                value={round}
                                onChange={handleRoundChange}
                                disabled={!tournamentId || rounds.length === 0}
                            >
                                {!tournamentId ? (
                                    <option value="">— Nejdřív turnaj —</option>
                                ) : rounds.length === 0 ? (
                                    <option value="">Žádné zápasy</option>
                                ) : (
                                    <>
                                        <option value="">— Vyberte kolo —</option>
                                        {rounds.map(r => (
                                            <option key={r} value={r}>{r === '—' ? 'Bez kola' : r}</option>
                                        ))}
                                    </>
                                )}
                            </select>
                        </div>
                        <div className="admin-field">
                            <label>Zápas</label>
                            <select
                                className="admin-select"
                                value={matchId}
                                onChange={handleMatchChange}
                                disabled={!round || matchesInRound.length === 0}
                            >
                                {!round ? (
                                    <option value="">— Nejdřív kolo —</option>
                                ) : matchesInRound.length === 0 ? (
                                    <option value="">Žádné zápasy v kole</option>
                                ) : (
                                    <>
                                        <option value="">— Vyberte zápas —</option>
                                        {matchesInRound.map(m => (
                                            <option key={m.id} value={m.id}>
                                                {formatMatchLabel(m)}
                                            </option>
                                        ))}
                                    </>
                                )}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Karta: Editace zápasu */}
                {loadingMatch && (
                    <div className="admin-action-card">
                        <p className="admin-action-desc">Načítám detail zápasu…</p>
                    </div>
                )}

                {!loadingMatch && loadedMatch && (
                    <div className="admin-action-card">
                        <div className="admin-action-header">
                            <span className="admin-action-icon"><FiEdit3 /></span>
                            <h3>Detail zápasu</h3>
                        </div>
                        <p className="admin-action-desc">
                            {formatMatchLabel(loadedMatch)}
                        </p>

                        <div className="admin-action-body">
                            <div className="admin-field">
                                <label>SportDB Match ID</label>
                                <input
                                    type="text"
                                    className="admin-input"
                                    value={loadedMatch.sportDbApiMatchId ?? '—'}
                                    readOnly
                                    disabled
                                />
                            </div>
                            <div className="admin-field">
                                <label>Football-API ID</label>
                                <input
                                    type="text"
                                    className="admin-input"
                                    value={loadedMatch.apiFootballId ?? '—'}
                                    readOnly
                                    disabled
                                />
                            </div>
                        </div>

                        <div className="admin-action-body">
                            <div className="admin-field">
                                <label>Datum a čas</label>
                                <input
                                    type="datetime-local"
                                    className="admin-input"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                />
                            </div>
                            <div className="admin-field">
                                <label>Stav zápasu</label>
                                <select
                                    className="admin-select"
                                    value={matchStatus}
                                    onChange={(e) => setMatchStatus(e.target.value)}
                                >
                                    {MATCH_STATUS_GROUPS.map(group => (
                                        <optgroup key={group.label} label={group.label}>
                                            {group.options.map(([value, label]) => (
                                                <option key={value} value={value}>{label}</option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                            </div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem', color: '#374151' }}>
                                <input
                                    type="checkbox"
                                    checked={canGoToOvertime}
                                    onChange={(e) => setCanGoToOvertime(e.target.checked)}
                                    disabled={saving}
                                    style={{ width: 16, height: 16, cursor: 'pointer' }}
                                />
                                Může jít do prodloužení (can go to overtime)
                            </label>
                        </div>

                        <div className="admin-score-section">
                            <span className="admin-field-group-title">Skóre</span>
                            <div className="admin-score-row">
                                <span className="admin-score-label">Konečné skóre</span>
                                <input type="number" min="0" className="admin-score-input"
                                    value={scoreHome} onChange={(e) => setScoreHome(e.target.value)} />
                                <span className="admin-score-sep">:</span>
                                <input type="number" min="0" className="admin-score-input"
                                    value={scoreAway} onChange={(e) => setScoreAway(e.target.value)} />
                            </div>
                            <div className="admin-score-row">
                                <span className="admin-score-label">Po 90 minutách</span>
                                <input type="number" min="0" className="admin-score-input"
                                    value={regHome} onChange={(e) => setRegHome(e.target.value)} />
                                <span className="admin-score-sep">:</span>
                                <input type="number" min="0" className="admin-score-input"
                                    value={regAway} onChange={(e) => setRegAway(e.target.value)} />
                            </div>
                            <div className="admin-score-row">
                                <span className="admin-score-label">Po prodloužení</span>
                                <input type="number" min="0" className="admin-score-input"
                                    value={etHome} onChange={(e) => setEtHome(e.target.value)} />
                                <span className="admin-score-sep">:</span>
                                <input type="number" min="0" className="admin-score-input"
                                    value={etAway} onChange={(e) => setEtAway(e.target.value)} />
                            </div>
                            <div className="admin-score-row">
                                <span className="admin-score-label">Penalty</span>
                                <input type="number" min="0" className="admin-score-input"
                                    value={penHome} onChange={(e) => setPenHome(e.target.value)} />
                                <span className="admin-score-sep">:</span>
                                <input type="number" min="0" className="admin-score-input"
                                    value={penAway} onChange={(e) => setPenAway(e.target.value)} />
                            </div>
                        </div>

                        <button
                            className="admin-action-btn"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Ukládám…' : <><FiSave /> Uložit a přepočítat body</>}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EditMatch;
